import { css } from "@emotion/css";
import Modal from "patient-ping-remedy/packages/modal";
import React, { useContext, useMemo, useState } from "react";
import Button, { StyleType } from "patient-ping-remedy/packages/button";
import Input from "patient-ping-remedy/packages/input";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ToastType } from "patient-ping-remedy/packages/toast";
import { AxiosError } from "axios";
import Helpers from "@/helpers/helpers";
import { CarecoApiContext } from "@/app-context/careco-api-context";
import { useAlertStore } from "@/store/alert_store";

interface Props {
  close: () => void;
  tableData: { name: { value: string } }[];
}

export default function CreateFormFieldNameModal({ close, tableData }: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const queryClient = useQueryClient();
  const { addAlert } = useAlertStore();
  const [formName, setFormName] = useState<string>("");

  const isValid = useMemo(() => {
    if (!formName) return true;
    const re = /^[A-Za-z0-9_'-]{2,200}$/;
    return !Helpers.isDefined(formName) || re.test(formName!);
  }, [formName]);

  const { mutate: addFormFieldName, isPending: isAdding } = useMutation({
    mutationFn: (name: string) => carecoApi!.createFormFieldName(name),
    onSuccess: () => {
      addAlert({ content: "Form field name added successfully.", type: ToastType.SUCCESS });
      void queryClient.invalidateQueries({ queryKey: ["formFieldNames"] });
    },
    onError: (error: AxiosError) => {
      Helpers.alertOnError(`Failed to add form field name. ${Helpers.traceId(error)}`, error);
    },
  });

  const handleAddFormName = () => {
    if (formName) {
      addFormFieldName(formName);
      close();
    }
  };

  const isFormNameTaken = useMemo(() => {
    return tableData.some((data) => data.name.value === formName);
  }, [formName, tableData]);

  const isAddDisabled =
    isFormNameTaken || isAdding || !formName || !isValid;

  return (
    <Modal
      className={css({ width: "70rem" })}
      headerText="Create form field name"
      buttons={[
        <Button key="cancel" onClick={close} styleType={StyleType.TERTIARY}>
          Cancel
        </Button>,
        <Button
          testId="add-form-field-name-modal-button"
          key="add"
          onClick={handleAddFormName}
          disabled={isAddDisabled}
        >
          Add
        </Button>,
      ]}
    >
      <Input
        value={formName}
        id="form-field-name"
        labelText="Field name"
        required="required"
        onChange={(e) => setFormName(e.target.value)}
        errorMessage={isFormNameTaken ? "Field name already exists" :
          "The field name must be between 2 and 200 characters long "
          + "containing only letters (A-Z, a-z), numbers (0-9), underscores (_),"
          + "and/or hyphens (-)."}
        error={!isValid || isFormNameTaken}
      />
    </Modal>
  );
}
